import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useLanguage } from '../../app/providers/LanguageProvider';
import './Nav.css';

const Nav = () => {
  const { t } = useTranslation();
  const { language, setLanguage, direction } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const dialogRef = useRef(null);
  const hamburgerRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setScrolled(window.scrollY > 16));
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  // Close drawer on route change
  useEffect(() => {
    if (dialogRef.current?.open) {
      dialogRef.current.close();
    }
  }, [location.pathname]);

  const openDrawer = () => {
    dialogRef.current?.showModal();
  };

  const closeDrawer = () => {
    dialogRef.current?.close();
    requestAnimationFrame(() => hamburgerRef.current?.focus());
  };

  const toggleLang = () => {
    setLanguage(language === 'ar' ? 'en' : 'ar');
  };

  const otherLangLabel = language === 'ar' ? 'English' : 'العربية';

  const handleBackdropClick = (event) => {
    if (event.target === dialogRef.current) {
      closeDrawer();
    }
  };

  return (
    <nav
      className={`raskh-nav${scrolled ? ' raskh-nav--scrolled' : ''}`}
      dir={direction}
      aria-label={t('navigation.aria', { defaultValue: 'Primary navigation' })}
    >
      <div className='raskh-nav__container'>
        <Link to='/' className='raskh-nav__brand'>
          <span className='raskh-nav__logo'>{t('appName')}</span>
        </Link>

        <ul className='raskh-nav__links raskh-nav__links--desktop'>
          <li>
            <a href='#why-section' className='raskh-nav__link'>{t('landing.nav.why')}</a>
          </li>
          <li>
            <a href='#how-section' className='raskh-nav__link'>{t('landing.nav.how')}</a>
          </li>
          <li>
            <a href='#pricing-section' className='raskh-nav__link'>{t('landing.nav.pricing')}</a>
          </li>
        </ul>

        <div className='raskh-nav__actions raskh-nav__actions--desktop'>
          <button
            type='button'
            className='raskh-nav__lang-toggle'
            onClick={toggleLang}
            aria-label={t('languageSwitcher.aria', { defaultValue: 'Switch language' })}
          >
            {otherLangLabel}
          </button>
          <Link to='/login' className='raskh-nav__login'>{t('landing.nav.login')}</Link>
          <Link to='/signup' className='raskh-nav__signup'>{t('landing.nav.signup')}</Link>
        </div>

        <button
          ref={hamburgerRef}
          type='button'
          className='raskh-nav__hamburger'
          onClick={openDrawer}
          aria-label={t('navigation.menu', { defaultValue: 'Open menu' })}
        >
          <svg
            width='30'
            height='30'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
            aria-hidden='true'
          >
            <line x1='3' y1='6' x2='21' y2='6' />
            <line x1='3' y1='12' x2='21' y2='12' />
            <line x1='3' y1='18' x2='21' y2='18' />
          </svg>
        </button>
      </div>

      <dialog
        ref={dialogRef}
        className='raskh-nav__dialog'
        dir={direction}
        onClick={handleBackdropClick}
        aria-label={t('navigation.menu', { defaultValue: 'Open menu' })}
      >
        <div className='raskh-nav__dialog-content'>
          <button
            type='button'
            className='raskh-nav__dialog-close'
            onClick={closeDrawer}
            aria-label={t('navigation.closeMenu', { defaultValue: 'Close menu' })}
          >
            ×
          </button>
          <ul className='raskh-nav__links raskh-nav__links--mobile'>
            <li>
              <a href='#why-section' onClick={closeDrawer}>{t('landing.nav.why')}</a>
            </li>
            <li>
              <a href='#how-section' onClick={closeDrawer}>{t('landing.nav.how')}</a>
            </li>
            <li>
              <a href='#pricing-section' onClick={closeDrawer}>{t('landing.nav.pricing')}</a>
            </li>
          </ul>
          <div className='raskh-nav__actions raskh-nav__actions--mobile'>
            <button
              type='button'
              onClick={() => {
                toggleLang();
                closeDrawer();
              }}
              aria-label={t('languageSwitcher.aria', { defaultValue: 'Switch language' })}
            >
              {otherLangLabel}
            </button>
            <Link to='/login' onClick={closeDrawer}>{t('landing.nav.login')}</Link>
            <Link to='/signup' onClick={closeDrawer}>{t('landing.nav.signup')}</Link>
          </div>
        </div>
      </dialog>
    </nav>
  );
};

export default Nav;
